import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Hero from "src/components/Hero/Hero";
import TitleSection from "src/components/TitleSection/TitleSection";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import Button from "src/components/Button/Button";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import { useWindowWidth } from "src/utils/windowUtils";

const ASSET = "entertainment/variety/new-years-eve-2027";
const GOLD = "#C39E43";
const GOLD_INK = "#161112";
const GOLD_FILL =
  "linear-gradient(90deg, #98690B 0%, #FFE891 50%, #98690B 100%)";

const goldButton = {
  bgColor: "#000",
  hoverBgColor: "#141414",
  color: GOLD,
  hoverColor: "#FFE891",
  size: "small",
  width: "compact",
  className: "no-spacing",
};

const RESTAURANTS = ["oro", "etc", "rail"];

const menuHref = (link) => {
  if (!link) return undefined;
  if (link.startsWith("/")) return `${window.location.origin}${link}`;
  return link;
};

const restaurantMedia = {
  oro: `${ASSET}/orso-buffet.jpg`,
  etc: `${ASSET}/etc-food.jpg`,
  rail: `${ASSET}/the-rail-food.jpg`,
};

const scrollToSection = (id) => (event) => {
  event.preventDefault();
  const section = document.getElementById(id);
  if (!section) return;
  section.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.pushState(null, "", `#${id}`);
};

const Photo = ({ src, alt, ratio = "16 / 10" }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <PhotoSlot $ratio={ratio} />;
  }

  return (
    <PhotoSlot $ratio={ratio}>
      <ResponsiveImage
        src={src}
        alt={alt}
        customStyles={{ width: "100%", height: "100%", objectFit: "cover" }}
        onError={() => setFailed(true)}
      />
    </PhotoSlot>
  );
};

const NewYearsEve2027 = () => {
  const { t, i18n } = useTranslation();
  const width = useWindowWidth();
  const lang = i18n.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
  const heroPath = `${ASSET}/NYE-2027-hero-image-${
    width < 992 ? "mobile" : "desktop"
  }-${lang}.jpg`;
  const backgroundImage = useResponsiveImageUrl(
    `${ASSET}/Black-background-with-red-gold-palm-fronds.png`
  );
  const introImage = useResponsiveImageUrl(
    `${ASSET}/Midnight-Miami-Palms-in-Burgundy-Glow.png`
  );
  const heroImage = useResponsiveImageUrl(heroPath);
  const [heroReady, setHeroReady] = useState(false);
  const copy = (key) => t(`entertainment.newYearsEve2027.${key}`);

  useEffect(() => {
    if (!heroImage) return undefined;
    let active = true;
    const img = new Image();
    img.onload = () => {
      if (active) setHeroReady(img.naturalWidth > 32);
    };
    img.onerror = () => {
      if (active) setHeroReady(false);
    };
    img.src = heroImage;
    return () => {
      active = false;
    };
  }, [heroImage]);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const tabs = [
    { id: "dining", label: copy("tabs.dining") },
    { id: "gaming", label: copy("tabs.gaming") },
    { id: "entertainment", label: copy("tabs.entertainment") },
  ];

  const drawTimes = copy("content.gaming.drawTimes")
    .split(/\||\n/)
    .map((time) => time.trim())
    .filter(Boolean);
  const djVenues = t(
    "entertainment.newYearsEve2027.content.entertainment.djs.venues",
    { returnObjects: true }
  );
  const freePlayTiers = t(
    "entertainment.newYearsEve2027.content.gaming.tiers",
    { returnObjects: true }
  );

  const showMenuLink = copy("content.dining.show.menuLink");

  const renderRestaurant = (key) => {
    const media = restaurantMedia[key];
    const menuLink = copy(`content.restaurants.${key}.menuLink`);
    const price = t(
      `entertainment.newYearsEve2027.content.restaurants.${key}.price`,
      { defaultValue: "" }
    );

    return (
      <Col xs={12} md={4} key={key}>
        <GoldFrame>
          <FrameInner>
            {media && (
              <Photo
                src={media}
                alt={copy(`content.restaurants.${key}.imageAlt`)}
                ratio="16 / 10"
              />
            )}
            <CardBody className={media ? undefined : "text-only"}>
              <h3>{copy(`content.restaurants.${key}.name`)}</h3>
              {price && <p className="price">{price}</p>}
              <ReactMarkdown>
                {copy(`content.restaurants.${key}.description`)}
              </ReactMarkdown>
              <MenuButton>
                <Button
                  {...goldButton}
                  href={menuHref(menuLink)}
                  label={copy(`content.restaurants.${key}.menuText`)}
                />
              </MenuButton>
            </CardBody>
          </FrameInner>
        </GoldFrame>
      </Col>
    );
  };

  return (
    <Page>
      <EventFinished scheduleKeyName="newYearsEve2027" eventType="event" />

      <Hero
        className={`nye-hero${heroReady ? " has-art" : ""}`}
        bgImage={heroPath}
        imageAlt={copy("hero.imageAlt")}
      />

      <TabWrap $drop={!heroReady}>
        <TabBar aria-label={copy("tabs.label")}>
          {tabs.map((tab) => (
            <a key={tab.id} href={`#${tab.id}`} onClick={scrollToSection(tab.id)}>
              {tab.label}
            </a>
          ))}
        </TabBar>
      </TabWrap>

      <IntroBand $image={introImage}>
        <container-div>
          <TitleSection
            as="h1"
            className="nye-title"
            title={copy("content.intro.title")}
            text={copy("content.intro.subtitle")}
            dark={false}
          />
          <Disclaimer>
            <ReactMarkdown>{copy("content.intro.disclaimer")}</ReactMarkdown>
          </Disclaimer>
        </container-div>
      </IntroBand>

      <container-div>
        <Section id="dining">
          <TitleSection
            as="h2"
            className="nye-title"
            title={copy("content.dining.title")}
            dark={false}
          />
          <DiningPanel>
            <DiningMedia>
              <GoldFrame>
                <Photo
                  src={`${ASSET}/dancing-through-the-decades.jpg`}
                  alt={copy("content.dining.show.imageAlt")}
                  ratio="4 / 3"
                />
              </GoldFrame>
            </DiningMedia>
            <DiningAside>
              <GoldFrame>
                <FrameInner>
                  <ShowCopy>
                    <p className="eyebrow">
                      {copy("content.dining.show.venueLabel")}
                    </p>
                    <h3>{copy("content.dining.show.title")}</h3>
                    <p className="show-name">
                      {copy("content.dining.show.showName")}
                    </p>
                    {copy("content.dining.show.description") ? (
                      <p>{copy("content.dining.show.description")}</p>
                    ) : null}
                    <p className="price">{copy("content.dining.show.price")}</p>
                    <MenuButton>
                      <Button
                        {...goldButton}
                        href={menuHref(showMenuLink)}
                        label={copy("content.dining.show.menuText")}
                      />
                    </MenuButton>
                  </ShowCopy>
                </FrameInner>
              </GoldFrame>
              <GoldFrame>
                <FrameInner>
                  <ShowCopy>
                    <h4>{copy("content.dining.show.seatingTitle")}</h4>
                    <SeatingTimes>
                      <div>
                        <span>{copy("content.dining.show.seating1.label")}</span>
                        <p>
                          {copy("content.dining.show.seating1.description")}
                        </p>
                        <strong>
                          {copy("content.dining.show.seating1.time")}
                        </strong>
                      </div>
                      <div>
                        <span>{copy("content.dining.show.seating2.label")}</span>
                        <p>
                          {copy("content.dining.show.seating2.description")}
                        </p>
                        <strong>
                          {copy("content.dining.show.seating2.time")}
                        </strong>
                      </div>
                    </SeatingTimes>
                  </ShowCopy>
                </FrameInner>
              </GoldFrame>
            </DiningAside>
          </DiningPanel>

          <Row className="g-3 restaurant-row">
            {RESTAURANTS.map((key) => renderRestaurant(key))}
          </Row>
          <PreSale>
            <ReactMarkdown>{copy("preSale.text")}</ReactMarkdown>
          </PreSale>
        </Section>
      </container-div>

      <Section id="gaming">
        <container-div>
          <TitleSection
            as="h2"
            className="nye-title nye-gaming-lead"
            title={copy("content.gaming.title")}
            text={copy("content.gaming.intro")}
            dark={false}
          />
        </container-div>
        <PromoStage>
          <Photo
            src={`${ASSET}/win-a-million-bg.jpg`}
            alt={copy("content.gaming.imageAlt")}
            ratio="1941 / 1047"
          />
          <MillionMark>
            <img
              src={`https://assets.playground.ca/pgfe/img/${ASSET}/win-a-milllion-2027-${lang}.png`}
              alt=""
            />
          </MillionMark>
          <PromoPanels>
            <InfoPanel>
              <div className="rules">
                <ReactMarkdown>{copy("content.gaming.rules")}</ReactMarkdown>
              </div>
              <p>{copy("content.gaming.rulesNote")}</p>
                <h3>{copy("content.gaming.drawTimesLabel")}</h3>
                <div className="fine">
                  <ReactMarkdown>{copy("content.gaming.drawNote")}</ReactMarkdown>
                </div>
                <TimeRow>
                  {drawTimes.map((time) => (
                    <span key={time}>{time}</span>
                  ))}
                </TimeRow>
            </InfoPanel>
            <InfoPanel>
              <h3 className="center">{copy("content.gaming.freePlayTitle")}</h3>
              <p className="highlight center">
                {copy("content.gaming.freePlayHighlight")}
              </p>
              <div className="freeplay center">
                <ReactMarkdown>
                  {copy("content.gaming.freePlayText")}
                </ReactMarkdown>
              </div>
              <EarnRow>
                <TierFrame>
                  <TierList>
                    {(Array.isArray(freePlayTiers) ? freePlayTiers : []).map(
                      (tier) => (
                        <li key={tier.name}>
                          <span>{tier.name}</span>
                          <strong>{tier.amount}</strong>
                        </li>
                      )
                    )}
                  </TierList>
                </TierFrame>
                <Thumb>
                  <GoldFrame>
                    <Photo
                      src={`${ASSET}/winning-wednesday-nye-en-1.jpg`}
                      alt={copy("content.gaming.earnImageAlt")}
                      ratio="5 / 4"
                    />
                  </GoldFrame>
                </Thumb>
              </EarnRow>
            </InfoPanel>
          </PromoPanels>
        </PromoStage>
      </Section>

      <container-div>
        <Section id="entertainment">
          <TitleSection
            as="h2"
            className="nye-title"
            title={copy("content.entertainment.title")}
            dark={false}
          />
          <GoldFrame>
            <DjSplit>
              <Photo
                src={`${ASSET}/nye-2027-dj.jpg`}
                alt={copy("content.entertainment.djs.imageAlt")}
                ratio="16 / 10"
              />
              <FrameInner>
                <ShowCopy className="dj-copy">
                  <h3>{copy("content.entertainment.djs.title")}</h3>
                  <p>{copy("content.entertainment.djs.description")}</p>
                  <VenueList>
                    {(Array.isArray(djVenues) ? djVenues : []).map((venue) => (
                      <li key={venue}>
                        <strong>{venue}</strong>
                      </li>
                    ))}
                  </VenueList>
                </ShowCopy>
              </FrameInner>
            </DjSplit>
          </GoldFrame>
        </Section>
      </container-div>

      <ClosingStage>
        <container-div>
          <Row className="g-4 entertainment-cards">
            <Col xs={12} md={6}>
              <GoldFrame>
                <FrameInner>
                  <Photo
                    src={`${ASSET}/entertainment-inside.jpg`}
                    alt={copy("content.entertainment.roaming.imageAlt")}
                    ratio="2.2 / 1"
                  />
                  <CardBody>
                    <h3>{copy("content.entertainment.roaming.title")}</h3>
                    <ReactMarkdown>
                      {copy("content.entertainment.roaming.description")}
                    </ReactMarkdown>
                  </CardBody>
                </FrameInner>
              </GoldFrame>
            </Col>
            <Col xs={12} md={6}>
              <GoldFrame>
                <FrameInner>
                  <Photo
                    src={`${ASSET}/champagne.jpg`}
                    alt={copy("content.entertainment.midnight.imageAlt")}
                    ratio="2.2 / 1"
                  />
                  <CardBody>
                    <h3>{copy("content.entertainment.midnight.title")}</h3>
                    <ReactMarkdown>
                      {copy("content.entertainment.midnight.description")}
                    </ReactMarkdown>
                  </CardBody>
                </FrameInner>
              </GoldFrame>
            </Col>
          </Row>
          <TitleSection
            as="h2"
            className="nye-title"
            title={copy("content.closing.title")}
            text={copy("content.closing.subtitle")}
            dark={false}
          />
        </container-div>
        <PalmOverflow $image={backgroundImage} />
      </ClosingStage>
    </Page>
  );
};

const Page = styled.div`
  background-color: #000;
  color: var(--white);

  .nye-hero {
    background-color: #000;
  }

  .nye-hero.has-art {
    min-height: 0;
    aspect-ratio: 3 / 1;
    padding-top: 0;
    background-position: center center;
  }

  .nye-hero .hero_text_container,
  .nye-hero .hero_image_container {
    display: none;
  }

  @media (min-width: 992px) {
    .nye-hero.has-art {
      margin-top: 140px;
    }
  }

  @media (max-width: 991px) {
    .nye-hero.has-art {
      aspect-ratio: 1 / 1;
    }
  }

  .nye-title.title-section {
    padding: 3.25rem 0 2rem;
  }

  .nye-title {
    h1,
    h2 {
      color: var(--white);
      font-size: 2.5rem;
    }

    p {
      font-size: 1.125rem;
      margin-bottom: 0;
    }
  }

  .nye-gaming-lead p {
    font-weight: 700;
  }

  @media (max-width: 767px) {
    .nye-title {
      h1,
      h2 {
        font-size: 2rem;
      }
    }
  }

  .walk-in {
    margin-top: 1.5rem;
  }

  .entertainment-cards {
    --bs-gutter-y: 0px;
    margin-top: 0.75rem;
  }

  .restaurant-row {
    margin-top: calc(0.75rem - var(--bs-gutter-y));
  }

  @media (max-width: 767px) {
    .entertainment-cards > [class*="col"] + [class*="col"] {
      margin-top: 1rem;
    }
  }
`;

const TabWrap = styled.div`
  width: 100%;
  margin: ${(props) => (props.$drop ? "52px" : "0")} auto 0;
`;

const TabBar = styled.nav`
  display: flex;
  gap: 1px;

  a {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0.65rem 0.5rem;
    background-color: #98690b;
    background-image: ${GOLD_FILL};
    color: ${GOLD_INK};
    font-weight: 700;
    font-size: clamp(0.72rem, 1.3vw, 0.95rem);
    letter-spacing: 0.06em;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;
    text-transform: uppercase;
  }

  a:hover,
  a:focus-visible {
    color: ${GOLD_INK};
    filter: brightness(1.08);
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const IntroBand = styled.div`
  width: 100%;
  padding: 2.5rem 0 3rem;
  background-image: ${(props) =>
    props.$image ? `url('${props.$image}')` : "none"};
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: 100% auto;

  @media (max-width: 767px) {
    padding: 0.5rem 0 0;
    background-size: 100% auto;
    background-position: center top;

    .nye-title.title-section {
      padding-top: 1.5rem;
    }
  }
`;

const ClosingStage = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 1.5rem;
`;

const PalmOverflow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  height: clamp(420px, 36vw, 620px);
  background-image: ${(props) =>
    props.$image ? `url('${props.$image}')` : "none"};
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: 100% auto;
  mix-blend-mode: screen;
  pointer-events: none;
`;

const Section = styled.section`
  scroll-margin-top: 160px;
`;

const Disclaimer = styled.div`
  max-width: 760px;
  margin: 0 auto 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--white);

  p,
  strong {
    margin: 0;
    color: var(--white);
    font-weight: 700;
  }

  @media (max-width: 767px) {
    margin-bottom: 0;
  }
`;

const GoldFrame = styled.div`
  border: 1px solid transparent;
  border-image: ${GOLD_FILL} 1;
  height: 100%;
  background: #000;
`;

const DjSplit = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  background: #000;
  min-height: 260px;

  > div:first-child {
    height: 100%;
    aspect-ratio: auto;
  }

  > div + div {
    border-left: 1px solid transparent;
    border-image: ${GOLD_FILL} 1;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;

    > div:first-child {
      aspect-ratio: 16 / 10;
      min-height: 200px;
    }

    > div + div {
      border-left-width: 0;
      border-top: 1px solid transparent;
      border-image: ${GOLD_FILL} 1;
    }
  }
`;

const DiningPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  align-items: stretch;

  @media (min-width: 768px) {
    grid-template-columns: 1.35fr 0.9fr;
  }
`;

const DiningMedia = styled.div`
  min-width: 0;

  @media (min-width: 768px) {
    > div {
      height: 100%;
    }

    > div > div {
      height: 100%;
      aspect-ratio: auto;
    }
  }
`;

const DiningAside = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;

  > h3 {
    color: var(--white);
    font-size: 1.45rem;
    font-weight: 700;
    margin: 0;
  }
`;

const MenuButton = styled.div`
  display: inline-flex;
  width: fit-content;
  margin: 0.15rem 0 0.35rem;
  border: 1px solid transparent;
  border-image: ${GOLD_FILL} 1;

  a,
  button {
    border-radius: 0;
  }
`;

const FrameInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #000;
`;

const PhotoSlot = styled.div`
  aspect-ratio: ${(props) => props.$ratio || "16 / 10"};
  background: #141414;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ShowCopy = styled.div`
  flex: 1;
  text-align: left;
  padding: 1.25rem 1.35rem 1.35rem;

  h3,
  h4 {
    color: var(--white);
    font-weight: 700;
    margin: 0 0 0.75rem;
  }

  h3 {
    font-size: 1.6rem;
  }

  h4 {
    margin-top: 1.25rem;
    font-size: 1.5rem;
  }

  h4:first-child {
    margin-top: 0;
  }

  &.dj-copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;

    h3 {
      font-size: 1.5rem;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      margin-top: 0.65rem;
    }
  }

  p {
    margin: 0 0 0.75rem;
  }

  .time {
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0 0 0.35rem;
  }

  .location {
    margin-bottom: 0.85rem;
  }

  .eyebrow {
    color: ${GOLD};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 0.4rem;
  }

  .show-name {
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0 0 0.15rem;
  }

  .price {
    color: ${GOLD};
    font-weight: 700;
  }

  @media (max-width: 767px) {
    text-align: center;
  }
`;

const SeatingTimes = styled.div`
  display: grid;
  gap: 0.85rem;

  span,
  strong,
  p {
    display: block;
  }

  p {
    margin: 0.2rem 0 0.35rem;
    font-size: 0.95rem;
    line-height: 1.35;
  }

  span {
    color: var(--white);
    font-size: 0.95rem;
  }

  @media (max-width: 767px) {
    text-align: center;
  }

  strong {
    font-size: 1.05rem;
  }

  > div + div {
    padding-top: 0.85rem;
    background-image: ${GOLD_FILL};
    background-repeat: no-repeat;
    background-position: left top;
    background-size: 100% 1px;
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding: 1rem 1rem 1.25rem;
  text-align: left;

  ${MenuButton} {
    margin-top: auto;
  }

  h3 {
    color: var(--white);
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0.15rem 0 0.35rem;
  }

  p {
    margin: 0 0 0.75rem;
    text-align: left;
  }

  .price {
    color: ${GOLD};
    font-weight: 700;
  }

  &.text-only {
    justify-content: center;
    padding: 1.5rem 1.25rem;
  }

  @media (max-width: 767px) {
    align-items: center;
    text-align: center;

    p {
      text-align: center;
    }
  }
`;

const PreSale = styled.div`
  margin-top: 2rem;
  padding: 1rem 1.5rem;
  background-color: #98690b;
  background-image: ${GOLD_FILL};
  color: var(--black);
  text-align: center;

  p {
    margin: 0;
    color: ${GOLD_INK};
  }

  p:not(:last-child) {
    font-size: 1.35rem;
    line-height: 1.3;
  }

  p + p {
    margin-top: 0.35rem;
  }
`;

const PromoStage = styled.div`
  position: relative;
  height: clamp(820px, 74vw, 1120px);
  overflow: hidden;

  > div:first-child {
    position: absolute;
    inset: 0;
    height: 100%;
    aspect-ratio: auto;
  }

  > div:first-child img {
    object-fit: cover;
    transform: scale(1.22);
    transform-origin: center 92%;
  }

  @media (max-width: 767px) {
    height: auto;
    overflow: visible;

    > div:first-child {
      position: relative;
      inset: auto;
      height: 72vw;
      min-height: 260px;
    }

    > div:first-child img {
      transform: none;
    }
  }
`;

const MillionMark = styled.div`
  position: absolute;
  top: 1.5%;
  left: 50%;
  z-index: 1;
  width: clamp(400px, 50vw, 780px);
  transform: translateX(-50%);
  pointer-events: none;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const PromoPanels = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  z-index: 2;
  width: min(1160px, calc(100% - 48px));
  transform: translateX(-50%);
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(0, 1fr);
  align-items: stretch;
  gap: 12px;

  @media (max-width: 767px) {
    position: static;
    width: auto;
    transform: none;
    margin: 0.75rem 20px 0;
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  background: #000;
  border: 1px solid transparent;
  border-image: ${GOLD_FILL} 1;
  padding: 1rem 1rem;
  text-align: left;

  h3 {
    color: var(--white);
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.85rem 0 0.3rem;
    text-align: left;
  }

  h3:first-child {
    margin-top: 0;
  }

  h3.center,
  .highlight.center,
  .freeplay.center,
  .freeplay.center p {
    text-align: center;
  }

  .rules,
  .rules p {
    color: var(--white);
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 1.35;
    margin: 0;
  }

  .rules strong {
    color: ${GOLD};
    font-weight: 700;
  }

  .rules p {
    margin-bottom: 0.35rem;
  }

  .highlight {
    color: ${GOLD};
    font-size: 1.15rem;
    font-weight: 700;
    margin: 0 0 0.45rem;
    text-align: left;
  }

  .fine,
  .fine p {
    color: var(--white);
    font-size: 0.92rem;
    font-weight: 400;
    margin: 0 0 0.55rem;
    text-align: left;
  }

  .fine strong {
    color: ${GOLD};
    font-weight: 700;
  }

  .freeplay p {
    margin: 0;
    text-align: left;
  }

  .freeplay p + p {
    margin-top: 0.15rem;
  }

  p {
    margin: 0 0 0.4rem;
  }

  @media (max-width: 767px) {
    text-align: center;

    h3,
    .highlight,
    .fine,
    .fine p,
    .freeplay p,
    .rules,
    .rules p {
      text-align: center;
    }
  }
`;

const TimeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.45rem;

  span {
    border: 1px solid transparent;
    border-image: ${GOLD_FILL} 1;
    padding: 0.45rem 0.2rem;
    font-size: 0.85rem;
    line-height: 1.2;
    text-align: center;
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const EarnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 196px;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 0.85rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr minmax(0, 46%);
  }
`;

const TierFrame = styled.div`
  border: 1px solid transparent;
  border-image: ${GOLD_FILL} 1;
`;

const TierList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  margin: 0;
  padding: 0.7rem 0.85rem;
  background: #000;

  li {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.1rem;
    align-items: center;
    padding: 0.15rem 0;
    font-size: 0.92rem;
    letter-spacing: 0.05em;
  }

  span {
    color: rgba(255, 255, 255, 0.92);
    text-align: right;
  }

  strong {
    color: ${GOLD};
    font-weight: 600;
    text-align: center;
  }
`;

const Thumb = styled.div`
  width: 196px;
  align-self: stretch;

  > div,
  > div > div {
    height: 100%;
    aspect-ratio: auto;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const VenueList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;

  li {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.4rem 0 0.05rem;
    background-image: ${GOLD_FILL};
    background-repeat: no-repeat;
    background-position: left top;
    background-size: 100% 1px;
  }

  strong {
    color: ${GOLD};
    font-size: 1rem;
    font-weight: 600;
  }

  span {
    color: rgba(255, 255, 255, 0.86);
    font-size: 0.92rem;
  }

  @media (max-width: 767px) {
    text-align: center;
  }
`;

export default NewYearsEve2027;
